import axiosService from '../utils/axios';
import { getRecentlyViewedProfileIds } from '../utils/storage';
import qs from 'qs';

// Reusable types for search and sort functionality
export type SortOption = 'relevance' | 'recent';

// Shared parameters between similar and search functions
interface BaseQueryParams {
  sortBy?: SortOption;
  pageParam?: number;
  limit?: number;
}

// For search-specific parameters
export interface SearchParams extends BaseQueryParams {
  query: string;
  skills?: string[];
  location?: string;
}

async function getProfile(profileId: string) {
    const { data } = await axiosService.get(`/profile/${profileId}/`);
    return data;
}

async function getProfileFromUsername(username: string) {
    try {
        const { data } = await axiosService.get(`/profile/username/${username}`);
        return data;
    } catch (error: any) {
        console.error('API Error:', error.response?.data || error.message);
        throw error;
    }
}

async function getProfileFromUser(userId: string) {
    const { data } = await axiosService.get(`/profile/userId/${userId}`);
    return data;
}

async function getSimilarProfiles(
    userId: string,
    pageParam: number,
    sortBy: string,
    limit: number = 8 // Default to 8 for faster initial loading
) {
    try {
        // Create a cache key based on the parameters
        const cacheKey = `similar-profiles-${userId}-${pageParam}-${sortBy}-${limit}`;

        // Check for in-flight requests with the same parameters
        if ((getSimilarProfiles as any).pendingRequests?.[cacheKey]) {
            return (getSimilarProfiles as any).pendingRequests[cacheKey];
        }

        // Store the promise in the pending requests
        const requestPromise = (async () => {
            const requestParams = {
                pageParam,
                sortBy,
                limit,
                usePrecomputed: true, // Explicitly request precomputed scores
                context: 'discover' // Explicitly set context to 'discover' for the discover page
            };

            const { data } = await axiosService.get(`/profile/similar/${userId}`, {
                params: requestParams,
            });

            // Make sure we have a valid response
            if (!data) {
                console.error('No data returned from similar profiles API');
                return { profiles: [] };
            }

            // Handle different response formats
            let profiles: any[] = [];

            if (Array.isArray(data)) {
                // Handle array response
                profiles = data;
            } else if (data.profiles && Array.isArray(data.profiles)) {
                // Handle object with profiles property
                profiles = data.profiles;
            } else if (typeof data === 'object') {
                // Try to extract profiles from unknown object structure
                const possibleProfileArrays = Object.values(data).filter(val => Array.isArray(val)) as any[][];
                if (possibleProfileArrays.length > 0) {
                    // Use the longest array as the most likely profiles array
                    profiles = possibleProfileArrays.reduce((a, b) => a.length > b.length ? a : b, []);
                } else {
                    console.error('Could not identify profiles array in response:', data);
                }
            }

            // Always return a consistent structure
            return {
                profiles,
                nextPage: data.nextPage || null,
                total: data.total || profiles.length,
                usingPrecomputed: data.usingPrecomputed,
                recentSortLimitReached: data.recentSortLimitReached || false
            };
        })();

        // Initialize pendingRequests if it doesn't exist
        if (!(getSimilarProfiles as any).pendingRequests) {
            (getSimilarProfiles as any).pendingRequests = {};
        }

        // Store the promise
        (getSimilarProfiles as any).pendingRequests[cacheKey] = requestPromise;

        // Clear from pendingRequests when done
        const result = await requestPromise;
        delete (getSimilarProfiles as any).pendingRequests[cacheKey];

        return result;
    } catch (error: any) {
        console.error('Error fetching similar profiles:', error.response?.data || error.message);
        // Return empty array instead of throwing to avoid component crashes
        return { profiles: [] };
    }
}

async function getFeaturedProfiles(userId: string, limit?: number) {
    try {
        // Get viewed profiles from client storage
        const viewedProfileIds = getRecentlyViewedProfileIds();

        // Add viewed profiles from the client to the query params
        const { data } = await axiosService.get(`/profile/featured/${userId}`, {
            params: {
                useClientCache: true,
                limit,
                // The server can use this to prioritize profiles not in this list
                alreadyViewed: viewedProfileIds.length ? viewedProfileIds : undefined
            },
            paramsSerializer: params => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            }
        });
        return data;
    } catch (error) {
        console.error('API error in getFeaturedProfiles:', error);
        throw error;
    }
}

async function searchProfiles(params: string | SearchParams) {
    // Support both old usage (string only) and new enhanced usage (object)
    if (typeof params === 'string') {
        const { data } = await axiosService.get('/profile/search', {
            params: {
                query: params,
                context: 'search'  // Always identify this as search context
            }
        });
        return data;
    }

    // New implementation with additional params
    const { query, ...otherParams } = params;

    const { data } = await axiosService.get('/profile/search', {
        params: {
            query,
            context: 'search',  // Always identify this as search context
            ...otherParams
        },
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    });

    return data;
}

export { getProfile, getProfileFromUser, getProfileFromUsername, getSimilarProfiles, getFeaturedProfiles, searchProfiles };
